import {
  cnpjToApiPayload,
  formatCnpjMasked,
  normalizeCnpjAlphanumeric,
} from '@solfacil/frontend-utils/cnpj';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, watch } from 'vue';
import { z } from 'zod';

import useSearchParamsState from '~/hooks/useSearchParamsState';
import date from '~/utils/date';

import type { InactiveAccounts, UseInactiveAccountsParams } from '../types';

import inactiveAccountsFiltersSchema from '../schemas/filtersSchema';
import { convertToInputFormat, convertToStorageFormat } from '../utils/formatHelpers';

type InactiveAccountsFilterForm = z.infer<typeof inactiveAccountsFiltersSchema>;

const INITIAL_FILTER_STATE: UseInactiveAccountsParams = {
  competency_month: date().format('MM-YYYY'),
  page: 1,
  page_size: 20,
  partner_cnpj: '',
  partner_id: '',
  processing_status: 'PENDING_APPROVAL' as InactiveAccounts.ProcessingStatus,
  status_solfacil: '',
};

export default function useInactiveAccountsFilters(options?: {
  onFiltersChange?: () => void;
}) {
  const searchParams = useSearchParamsState<UseInactiveAccountsParams>(
    INITIAL_FILTER_STATE,
    { parseArrays: false },
  );

  const filterForm = useForm<InactiveAccountsFilterForm>({
    initialValues: {
      competency_month: convertToInputFormat(
        searchParams.value.competency_month || INITIAL_FILTER_STATE.competency_month,
      ),
      partner_cnpj: searchParams.value.partner_cnpj
        ? formatCnpjMasked(normalizeCnpjAlphanumeric(searchParams.value.partner_cnpj))
        : '',
      partner_id: searchParams.value.partner_id || '',
      processing_status: searchParams.value.processing_status || INITIAL_FILTER_STATE.processing_status,
      status_solfacil: searchParams.value.status_solfacil || '',
    },
    validationSchema: toTypedSchema(inactiveAccountsFiltersSchema),
  });

  const apiParams = computed(() => {
    const params = searchParams.value;
    const filteredParams: Partial<InactiveAccounts.GetInactiveAccountsRequest> = {};

    if (params.competency_month) filteredParams.competency_month = params.competency_month;
    if (params.processing_status) filteredParams.processing_status = params.processing_status;

    if (params.partner_cnpj?.trim()) filteredParams.partner_cnpj = params.partner_cnpj;
    if (params.partner_id?.trim()) {
      const partnerId = parseInt(params.partner_id.trim(), 10);
      if (!Number.isNaN(partnerId)) {
        filteredParams.partner_id = partnerId;
      }
    }
    if (params.status_solfacil && typeof params.status_solfacil === 'string' && params.status_solfacil.trim()) {
      filteredParams.status_solfacil = params.status_solfacil as InactiveAccounts.StatusSolfacil;
    }

    filteredParams.page = Number(params.page) || 1;
    filteredParams.page_size = Number(params.page_size) || 20;

    return filteredParams as InactiveAccounts.GetInactiveAccountsRequest;
  });

  function applyFiltersAutomatically(values: any) {
    const filteredValues: Partial<UseInactiveAccountsParams> = {
      page: 1,
      page_size: Number(searchParams.value.page_size) || 20,
    };

    if (values.competency_month) {
      filteredValues.competency_month = convertToStorageFormat(values.competency_month);
    }
    if (values.processing_status) filteredValues.processing_status = values.processing_status;

    if (values.partner_cnpj?.trim()) {
      filteredValues.partner_cnpj = cnpjToApiPayload(values.partner_cnpj);
    }
    if (values.partner_id?.trim()) {
      const partnerId = parseInt(values.partner_id.trim(), 10);
      if (!Number.isNaN(partnerId)) {
        filteredValues.partner_id = partnerId.toString();
      }
    } else {
      filteredValues.partner_id = '';
    }

    filteredValues.status_solfacil = values.status_solfacil || '';

    searchParams.value = filteredValues as UseInactiveAccountsParams;

    if (options?.onFiltersChange) {
      options.onFiltersChange();
    }
  }

  watch(() => filterForm.values.competency_month, (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
      applyFiltersAutomatically(filterForm.values);
    }
  });

  watch(() => filterForm.values.processing_status, (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
      applyFiltersAutomatically(filterForm.values);
    }
  });

  watch(() => filterForm.values.partner_cnpj, (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (!newValue || (newValue && normalizeCnpjAlphanumeric(newValue).length === 14)) {
        applyFiltersAutomatically(filterForm.values);
      }
    }
  });

  watch(() => filterForm.values.partner_id, (newValue, oldValue) => {
    if (newValue !== oldValue) {
      if (!newValue || (newValue && !Number.isNaN(parseInt(newValue.trim(), 10)))) {
        applyFiltersAutomatically(filterForm.values);
      }
    }
  });

  watch(() => filterForm.values.status_solfacil, (newValue, oldValue) => {
    if (newValue !== oldValue) {
      applyFiltersAutomatically(filterForm.values);
    }
  });

  function handleSearch() {
    return filterForm.handleSubmit((values) => {
      applyFiltersAutomatically(values);
    })();
  }

  function handleReset() {
    searchParams.value = {
      competency_month: INITIAL_FILTER_STATE.competency_month,
      page: 1,
      page_size: 20,
      partner_cnpj: '',
      partner_id: '',
      processing_status: INITIAL_FILTER_STATE.processing_status,
      status_solfacil: '',
    } as UseInactiveAccountsParams;

    filterForm.resetForm({
      values: {
        competency_month: convertToInputFormat(INITIAL_FILTER_STATE.competency_month),
        partner_cnpj: '',
        partner_id: '',
        processing_status: INITIAL_FILTER_STATE.processing_status,
        status_solfacil: '',
      },
    });
  }

  function setPage(value: number) {
    const currentParams = { ...searchParams.value };
    currentParams.page = Number(value);
    searchParams.value = currentParams;
  }

  function setPageSize(value: number) {
    const currentParams = { ...searchParams.value };
    currentParams.page_size = Number(value);
    currentParams.page = 1;
    searchParams.value = currentParams;
  }

  function initializeForm() {
    filterForm.setValues({
      competency_month: convertToInputFormat(
        searchParams.value.competency_month || INITIAL_FILTER_STATE.competency_month,
      ),
      partner_cnpj: searchParams.value.partner_cnpj
        ? formatCnpjMasked(normalizeCnpjAlphanumeric(searchParams.value.partner_cnpj))
        : '',
      partner_id: searchParams.value.partner_id || '',
      processing_status: searchParams.value.processing_status || INITIAL_FILTER_STATE.processing_status,
      status_solfacil: searchParams.value.status_solfacil || '',
    });
  }

  return {
    apiParams,
    filterForm,
    handleReset,
    handleSearch,
    initializeForm,
    searchParams,
    setPage,
    setPageSize,
  };
}
